import { playDtmfTone, primeCallTones, startRingbackTone, startRingtone, startCallWaitingTone } from "@/lib/callTones";
import {
  MicErrorKind, buildCallTarget, classifyMediaError, createSoftphoneUser,
  SoftphoneUser, SoftphoneCall, SoftphoneSnapshot,
} from "@/lib/sip";
import { useGetMyLineQuery, useLazyGetMyLineCredentialsQuery } from "@/services/softphone";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export type SoftphoneRegistrationState = "disconnected" | "connecting" | "registered" | "error";
// Aggregate state remains busy while ANY session exists, including held calls.
export type SoftphoneCallState = "idle" | "calling" | "in-call";
interface SoftphoneContextValue {
  hasLine: boolean;
  registrationState: SoftphoneRegistrationState;
  callState: SoftphoneCallState;
  calls: SoftphoneCall[];
  activeCall: SoftphoneCall | null;
  incomingCall: SoftphoneCall | null;
  busy: boolean;
  callError: string | null;
  micError: MicErrorKind | null;
  muted: boolean;
  extensionNumber: string | null;
  call(number: string): Promise<void>;
  answer(id?: string): Promise<void>;
  hangup(id?: string): Promise<void>;
  hold(id: string): Promise<void>;
  resume(id: string): Promise<void>;
  mute(muted: boolean): void;
  sendDtmf(digit: string): void;
}
const EMPTY: SoftphoneSnapshot = { calls: [], activeCallId: null, busy: false };
const SoftphoneContext = createContext<SoftphoneContextValue | undefined>(undefined);

export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { data: line } = useGetMyLineQuery(undefined, { pollingInterval: 5000, refetchOnFocus: true, refetchOnReconnect: true });
  const [fetchCredentials] = useLazyGetMyLineCredentialsQuery();
  const [registrationState, setRegistrationState] = useState<SoftphoneRegistrationState>("disconnected");
  const [snapshot, setSnapshot] = useState<SoftphoneSnapshot>(EMPTY);
  const snapshotRef = useRef(snapshot);
  const [micError, setMicError] = useState<MicErrorKind | null>(null);
  const [callError, setCallError] = useState<string | null>(null);
  const [extensionNumber, setExtensionNumber] = useState<string | null>(null);
  const userRef = useRef<SoftphoneUser | null>(null);
  const domainRef = useRef<string | null>(null);
  const hasLine = !!line && line.status === "active" && line.callingAvailable !== false;
  const { calls, busy } = snapshot;
  const activeCall = calls.find((call) => call.id === snapshot.activeCallId) ?? null;
  const incomingCall = calls.find((call) => call.state === "ringing") ?? null;
  const callState: SoftphoneCallState = !calls.length ? "idle"
    : calls.some((call) => !["ringing", "dialing"].includes(call.state)) ? "in-call" : "calling";

  useEffect(() => {
    if (!hasLine || !line) return;
    let cancelled = false;
    setRegistrationState("connecting");
    (async () => {
      try {
        const credentials = await fetchCredentials().unwrap();
        if (cancelled) return;
        const domain = credentials.domain.replace(/^wss?:\/\//, "").replace(/\/$/, "");
        domainRef.current = domain;
        setExtensionNumber(credentials.extensionNumber);
        const user = createSoftphoneUser({
          wssUri: `wss://${domain}/`, server: domain,
          username: credentials.extensionNumber, password: credentials.extensionPassword,
          delegate: {
            onCallsChanged: (next) => {
              if (cancelled) return;
              snapshotRef.current = next;
              setSnapshot(next);
            },
            onRegistered: () => { if (!cancelled) setRegistrationState("registered"); },
            onUnregistered: () => { if (!cancelled) setRegistrationState("disconnected"); },
            onServerDisconnect: () => {
              if (!cancelled) {
                setRegistrationState("connecting");
                setCallError("Connexion téléphonique interrompue. Reconnexion en cours…");
              }
            },
          },
        });
        userRef.current = user;
        await user.connect();
        if (cancelled) { await user.disconnect().catch(() => {}); return; }
        await user.register();
        if (cancelled) await user.disconnect().catch(() => {});
      } catch {
        if (!cancelled) setRegistrationState("error");
      }
    })();
    return () => {
      cancelled = true;
      const user = userRef.current;
      userRef.current = null;
      // disconnect stops the UserAgent, unregisters and closes every session.
      if (user) void user.disconnect().catch(() => {});
      domainRef.current = null;
      snapshotRef.current = EMPTY;
      setSnapshot(EMPTY);
      setExtensionNumber(null);
      setRegistrationState("disconnected");
      setCallError(null);
      setMicError(null);
    };
  }, [hasLine, line?.id, fetchCredentials]);

  const dialingId = activeCall?.state === "dialing" ? activeCall.id : null;
  useEffect(() => { if (dialingId) return startRingbackTone(); }, [dialingId]);
  useEffect(() => { if (registrationState === "registered") primeCallTones(); }, [registrationState]);
  const incomingId = incomingCall?.id;
  const hasForegroundCall = !!activeCall;
  useEffect(() => {
    if (!incomingId) return;
    return hasForegroundCall ? startCallWaitingTone() : startRingtone();
  }, [incomingId, hasForegroundCall]);

  const perform = useCallback(async (operation: (user: SoftphoneUser) => Promise<void>) => {
    const user = userRef.current;
    if (!user) throw new Error("Téléphone déconnecté");
    setCallError(null);
    setMicError(null);
    try { await operation(user); }
    catch (error) {
      if (userRef.current === user) {
        setMicError(classifyMediaError(error));
        setCallError(error instanceof Error ? error.message : "Impossible d’effectuer cette opération d’appel");
      }
      throw error;
    }
  }, []);
  const call = useCallback((number: string) => perform((user) => {
    if (!domainRef.current) throw new Error("Téléphone déconnecté");
    return user.call(buildCallTarget(domainRef.current, number));
  }), [perform]);
  const answer = useCallback((id?: string) => perform((user) => {
    const target = id ?? snapshotRef.current.calls.find((call) => call.state === "ringing")?.id;
    if (!target) throw new Error("Cet appel ne sonne plus");
    return user.answer(target);
  }), [perform]);
  const hangup = useCallback((id?: string) => perform((user) => {
    const target = id ?? snapshotRef.current.activeCallId ?? snapshotRef.current.calls[0]?.id;
    if (!target) throw new Error("Cet appel est terminé");
    return user.hangup(target);
  }), [perform]);
  const hold = useCallback((id: string) => perform((user) => user.hold(id)), [perform]);
  const resume = useCallback((id: string) => perform((user) => user.resume(id)), [perform]);
  const mute = useCallback((muted: boolean) => userRef.current?.mute(muted), []);
  const sendDtmf = useCallback((digit: string) => {
    const current = snapshotRef.current;
    if (current.busy || !current.calls.some((call) => call.id === current.activeCallId && call.state === "active")) return;
    playDtmfTone(digit);
    void perform((user) => user.sendDTMF(digit)).catch(() => {});
  }, [perform]);

  const value = useMemo<SoftphoneContextValue>(() => ({
    hasLine, registrationState, callState, calls, activeCall, incomingCall, busy,
    callError, micError, muted: activeCall?.muted ?? false, extensionNumber,
    call, answer, hangup, hold, resume, mute, sendDtmf,
  }), [hasLine, registrationState, callState, calls, activeCall, incomingCall, busy,
    callError, micError, extensionNumber, call, answer, hangup, hold, resume, mute, sendDtmf]);
  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}
export function useSoftphone(): SoftphoneContextValue {
  const context = useContext(SoftphoneContext);
  if (context === undefined) throw new Error("useSoftphone must be used within a SoftphoneProvider");
  return context;
}
